import React, { useCallback, useEffect, useState } from 'react';
import axios from '../../interceptors/adminAxios';
import AdminCompanyDetail from './AdminCompanyDetail';
import { useConfirm } from '../../hooks/useConfirm';

interface Company {
  id: number;
  name: string;
  display_name: string;
  color: string;
  description: string;
  tariff: string | null;
  is_blocked: boolean;
  user_count: number;
  device_count: number;
  online_count: number;
  created_at: string;
}

const COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#7c3aed','#ec4899','#0891b2','#84cc16'];
const TARIFF_LABELS: Record<string, string> = { free: 'Free', starter: 'Starter', business: 'Business', enterprise: 'Enterprise' };

const mfInput: React.CSSProperties = {
  width: '100%', background: 'var(--input-bg)', border: '1px solid var(--input-border)',
  borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text-primary)',
  fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
};

const btnAccent: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 13px', borderRadius: 8, border: 'none',
  background: 'var(--accent)', color: '#fff',
  fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
};

const btnGhost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 13px', borderRadius: 8,
  border: '1px solid var(--glass-border)',
  background: 'transparent', color: 'var(--text-secondary)',
  fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
};

const Mf: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-disabled)', marginBottom: 5 }}>{label}</div>
    {children}
  </div>
);

interface CoModalProps { onClose: () => void; onSave: () => void; initial?: Company | null; }

const CoModal: React.FC<CoModalProps> = ({ onClose, onSave, initial }) => {
  const [name, setName]      = useState(initial?.name ?? '');
  const [displayName, setDN] = useState(initial?.display_name ?? '');
  const [desc, setDesc]      = useState(initial?.description ?? '');
  const [color, setColor]    = useState(initial?.color ?? COLORS[0]);
  const [saving, setSaving]  = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (initial) {
        await axios.patch(`/admin/companies/${initial.id}`, { display_name: displayName || name, description: desc, color });
      } else {
        await axios.post('/admin/companies', { name: name.trim(), display_name: displayName || name, description: desc, color });
      }
      onSave();
    } catch {} finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(6px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 14, padding: 24, width: 440, boxShadow: '0 24px 64px rgba(0,0,0,.6)' }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 18, color: 'var(--text-primary)' }}>
          {initial ? 'Edit Company' : 'New Company'}
        </div>
        <Mf label="Company ID (slug)">
          <input value={name} onChange={e => setName(e.target.value)} disabled={!!initial} placeholder="e.g. mega" style={mfInput} />
        </Mf>
        <Mf label="Display Name">
          <input value={displayName} onChange={e => setDN(e.target.value)} placeholder="e.g. Mega Media" style={mfInput} />
        </Mf>
        <Mf label="Description">
          <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Optional" style={mfInput} />
        </Mf>
        <Mf label="Color">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {COLORS.map(c => (
              <div key={c} onClick={() => setColor(c)} style={{ width: 24, height: 24, borderRadius: '50%', background: c, cursor: 'pointer', border: color === c ? '2px solid #fff' : '2px solid transparent', boxShadow: color === c ? `0 0 0 2px ${c}` : 'none' }} />
            ))}
          </div>
        </Mf>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button style={btnGhost} onClick={onClose}>Cancel</button>
          <button style={btnAccent} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : initial ? 'Save' : 'Create Company'}</button>
        </div>
      </div>
    </div>
  );
};

// ── KPI card ─────────────────────────────────────────────────────────────────
const Kpi: React.FC<{ num: number | string; label: string; color: string; glow: string }> = ({ num, label, color, glow }) => (
  <div style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid var(--glass-border)', borderRadius: 14, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', top: -16, right: -16, width: 64, height: 64, borderRadius: '50%', filter: 'blur(18px)', background: glow, pointerEvents: 'none' }} />
    <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', marginBottom: 3, color }}>{num}</div>
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)' }}>{label}</div>
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const AdminCompaniesPage: React.FC = () => {
  const { confirm, confirmModal } = useConfirm();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editCompany, setEditCompany] = useState<Company | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [blocking, setBlocking] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try { const { data } = await axios.get<Company[]>('/admin/companies'); setCompanies(data); }
    catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (c: Company) => {
    if (!await confirm({ title: `Delete company "${c.display_name || c.name}"?`, message: 'This cannot be undone.', confirmLabel: 'Delete', cancelLabel: 'Cancel', variant: 'danger' })) return;
    setDeleting(c.id);
    try { await axios.delete(`/admin/companies/${c.id}`); load(); }
    catch {} finally { setDeleting(null); }
  };

  const handleBlock = async (c: Company) => {
    const action = c.is_blocked ? 'Unblock' : 'Block';
    if (!await confirm({ title: `${action} company "${c.display_name || c.name}"?`, confirmLabel: action, cancelLabel: 'Cancel' })) return;
    setBlocking(c.id);
    try { await axios.patch(`/admin/companies/${c.id}/block`, { is_blocked: !c.is_blocked }); load(); }
    catch {} finally { setBlocking(null); }
  };

  if (selectedId !== null) {
    const co = companies.find(c => c.id === selectedId);
    return <AdminCompanyDetail companyId={selectedId} company={co ?? null} onBack={() => { setSelectedId(null); load(); }} />;
  }

  const totalUsers   = companies.reduce((s, c) => s + c.user_count, 0);
  const totalDevices = companies.reduce((s, c) => s + c.device_count, 0);
  const totalOnline  = companies.reduce((s, c) => s + c.online_count, 0);
  const totalOffline = totalDevices - totalOnline;

  const filtered = companies.filter(c =>
    (c.display_name || c.name).toLowerCase().includes(search.toLowerCase()) ||
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {/* Topbar */}
      <div style={{ height: 50, flexShrink: 0, background: 'var(--topbar-bg)', borderBottom: '1px solid var(--divider)', display: 'flex', alignItems: 'center', padding: '0 20px', gap: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700, flex: 1, color: 'var(--text-primary)' }}>Companies</div>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search…"
          style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 8, padding: '5px 10px', fontSize: 12, color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit', width: 180 }}
        />
        <button style={btnGhost} onClick={() => { setEditCompany(null); setShowModal(true); }}>
          + New Company
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <Kpi num={companies.length} label="Companies"      color="var(--accent-light)"  glow="rgba(2,132,199,.25)" />
          <Kpi num={totalDevices}     label="Devices"        color="#34d399"               glow="rgba(52,211,153,.2)" />
          <Kpi num={totalUsers}       label="Users"          color="#a78bfa"               glow="rgba(167,139,250,.2)" />
          <Kpi num={totalOffline}     label="Offline devices" color="#f87171"              glow="rgba(248,113,113,.18)" />
        </div>

        {/* Companies table */}
        <div style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid var(--glass-border)', borderRadius: 14, overflow: 'hidden' }}>

          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 100px 70px 70px 80px 140px', padding: '8px 14px', background: 'var(--glass-hover)', borderBottom: '1px solid var(--divider)' }}>
            {['', 'Company', 'Tariff', 'Devices', 'Users', 'Status', 'Actions'].map((h, i) => (
              <div key={i} style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)' }}>{h}</div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div style={{ padding: '20px 18px', fontSize: 13, color: 'var(--text-disabled)' }}>No companies found.</div>
          )}

          {filtered.map(c => (
            <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '36px 1fr 100px 70px 70px 80px 140px', padding: '9px 14px', borderBottom: '1px solid var(--divider)', alignItems: 'center', opacity: c.is_blocked ? 0.6 : 1, transition: 'background .1s', cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--row-hover)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              onClick={() => setSelectedId(c.id)}
            >
              {/* Color avatar */}
              <div style={{ width: 26, height: 26, borderRadius: 7, background: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                {(c.display_name || c.name).charAt(0).toUpperCase()}
              </div>

              {/* Name + slug */}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.display_name || c.name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-disabled)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
              </div>

              {/* Tariff */}
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: 'rgba(167,139,250,.12)', color: '#a78bfa', border: '1px solid rgba(167,139,250,.25)' }}>
                  {TARIFF_LABELS[c.tariff ?? 'free'] ?? c.tariff ?? 'Free'}
                </span>
              </div>

              {/* Devices */}
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                <span style={{ color: '#4ade80', fontWeight: 600 }}>{c.online_count}</span>
                <span style={{ color: 'var(--text-disabled)' }}>/{c.device_count}</span>
              </div>

              {/* Users */}
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{c.user_count}</div>

              {/* Status badge */}
              <div>
                {c.is_blocked
                  ? <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'rgba(248,113,113,.12)', color: '#f87171', border: '1px solid rgba(248,113,113,.25)' }}>Blocked</span>
                  : <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'rgba(52,211,153,.1)', color: '#34d399', border: '1px solid rgba(52,211,153,.2)' }}>Active</span>
                }
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
                <button style={{ ...btnGhost, padding: '3px 8px', fontSize: 11 }} onClick={() => { setEditCompany(c); setShowModal(true); }}>Edit</button>
                <button
                  style={{ ...btnGhost, padding: '3px 8px', fontSize: 11, color: c.is_blocked ? '#34d399' : '#fbbf24', borderColor: c.is_blocked ? 'rgba(52,211,153,.25)' : 'rgba(251,191,36,.25)' }}
                  onClick={() => handleBlock(c)} disabled={blocking === c.id}
                >
                  {c.is_blocked ? 'Unblock' : 'Block'}
                </button>
                <button style={{ ...btnGhost, padding: '3px 8px', fontSize: 11, color: '#f87171', borderColor: 'rgba(248,113,113,.25)' }}
                  onClick={() => handleDelete(c)} disabled={deleting === c.id}>✕</button>
              </div>
            </div>
          ))}

          {/* Add row */}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', color: 'var(--text-disabled)', cursor: 'pointer', borderTop: filtered.length ? '1px solid var(--divider)' : 'none', transition: 'all .12s' }}
            onClick={() => { setEditCompany(null); setShowModal(true); }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.background = 'var(--row-hover)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-disabled)'; e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Add Company</span>
          </div>
        </div>
      </div>

      {showModal && (
        <CoModal
          initial={editCompany}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); load(); }}
        />
      )}
      {confirmModal}
    </>
  );
};

export default AdminCompaniesPage;
